import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const iconOptions = [
  'Building2', 'MapPin', 'Users', 'TrendingUp', 'Award', 'Target',
  'Briefcase', 'Globe', 'Zap', 'Star'
];

export default function AdminMilestones() {
  const [milestones, setMilestones] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    icon: 'Building2',
    order: 0
  });

  useEffect(() => {
    fetchMilestones();
  }, []);

  const fetchMilestones = async () => {
    try {
      const response = await apiClient.get('/milestones');
      setMilestones(response.data);
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        id: editingMilestone?.id || undefined
      };

      if (editingMilestone) {
        await apiClient.put(`/admin/milestones/${editingMilestone.id}`, payload);
        toast.success('Milestone updated successfully!');
      } else {
        await apiClient.post('/admin/milestones', payload);
        toast.success('Milestone created successfully!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchMilestones();
    } catch (error) {
      toast.error('Failed to save milestone');
    }
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone);
    setFormData({
      value: milestone.value,
      label: milestone.label,
      icon: milestone.icon,
      order: milestone.order
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this milestone?')) {
      try {
        await apiClient.delete(`/admin/milestones/${id}`);
        toast.success('Milestone deleted successfully!');
        fetchMilestones();
      } catch (error) {
        toast.error('Failed to delete milestone');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      value: '',
      label: '',
      icon: 'Building2',
      order: 0
    });
    setEditingMilestone(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Manage Milestones</h1>
              <p className="text-gray-600 mt-1">Edit the statistics shown on homepage (50,000+, 2 Locations, 150+ Clients)</p>
            </div>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Milestone
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {milestones.map((milestone) => (
            <div key={milestone.id} className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-4">
                <span className="text-white font-bold">{milestone.icon}</span>
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{milestone.value}</div>
              <div className="text-gray-600 mb-1">{milestone.label}</div>
              <span className="text-xs text-gray-500">Order: {milestone.order}</span>
              <div className="flex justify-center space-x-2 mt-4">
                <Button onClick={() => handleEdit(milestone)} size="sm" variant="outline">
                  <Edit size={16} className="mr-1" />
                  Edit
                </Button>
                <Button onClick={() => handleDelete(milestone.id)} size="sm" variant="destructive">
                  <Trash2 size={16} className="mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Value (e.g., "50,000+" or "2")</Label>
              <Input
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="50,000+"
                required
              />
            </div>
            <div>
              <Label>Label</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="sq. ft. of workspace"
                required
              />
            </div>
            <div>
              <Label>Icon</Label>
              <Select value={formData.icon} onValueChange={(value) => setFormData({ ...formData, icon: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select icon" />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((icon) => (
                    <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
